/**
 * Which choices of a configurable product are selectable for the current
 * configuration. Covers the two choice-level rules the public configurator
 * applies: choice constraints (allowed choices per row/column of a grid, e.g.
 * kapmaat per breedte x hoogte) and per-choice show conditions.
 *
 * Per-choice conditions are the same rules as option visibility
 * (`optionId` or `attribute`, `==` / `!=` / `equals` / …). Comparing them goes
 * through `evaluateCPQOptionRule`, so a choice the website allows is allowed
 * here too.
 *
 * Values are keyed by form field id (`${productId}-${optionId}-input`).
 * Numeric dimensions (number options and computed fields) are resolved by the
 * caller, so this module stays free of the pricing engine.
 */

import { evaluateCPQOptionRule } from './conditionsEvaluator.js';

const fieldIdFor = (product, optionId) => `${product.id}-${optionId}-input`;

const isChoiceOption = (option) =>
  option?.type === 'single-choice' || option?.type === 'multi-choice';

// A value above the last tier is outside the grid: no row or column matches.
function roundUpToTier(value, tiers) {
  if (!tiers.length) return value;
  for (const tier of tiers) {
    if (value <= tier) return tier;
  }
  return null;
}

function sameGridValue(gridValue, matched, isChoice) {
  if (!isChoice) return Number(gridValue) === Number(matched);
  if (Array.isArray(matched)) return matched.includes(String(gridValue));
  return String(gridValue) === String(matched);
}

function readChoiceValue(product, optionId, values) {
  const raw = values[fieldIdFor(product, optionId)];
  if (raw === undefined || raw === null || raw === '') return null;
  return Array.isArray(raw) ? raw.map(String) : String(raw);
}

function constraintAllowedIds(product, constraint, values, resolveNumericDimension) {
  const options = product.options || [];
  const rowIsChoice = isChoiceOption(options.find((o) => o.id === constraint.rowOptionId));
  const colIsChoice = isChoiceOption(options.find((o) => o.id === constraint.colOptionId));

  const rowTiers = rowIsChoice
    ? []
    : [...new Set(constraint.grid.map((row) => Number(row.rowValue)))]
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
  const colTiers =
    colIsChoice || !constraint.grid[0]?.cells
      ? []
      : [...new Set(constraint.grid[0].cells.map((cell) => Number(cell.colValue)))]
          .filter(Number.isFinite)
          .sort((a, b) => a - b);

  const resolve = (optionId, isChoice, data) =>
    isChoice
      ? readChoiceValue(product, optionId, data)
      : resolveNumericDimension(product, optionId, data);

  const colValue = resolve(constraint.colOptionId, colIsChoice, values);
  const matchedCol =
    colValue === null ? null : colIsChoice ? colValue : roundUpToTier(colValue, colTiers);
  if (colValue !== null && matchedCol === null) return new Set();

  const allowedForRow = (matchedRow) => {
    const row = constraint.grid.find((r) => sameGridValue(r.rowValue, matchedRow, rowIsChoice));
    if (!row?.cells?.length) return [];
    if (matchedCol !== null) {
      const cell = row.cells.find((c) => sameGridValue(c.colValue, matchedCol, colIsChoice));
      return cell?.allowedChoiceIds || [];
    }
    // Without a column value, a row-only input can already narrow the list.
    return [...new Set(row.cells.flatMap((cell) => cell.allowedChoiceIds || []))];
  };

  const target = options.find((o) => o.id === constraint.targetOptionId);
  const targetFieldId = fieldIdFor(product, constraint.targetOptionId);
  const allowed = new Set();

  // The row may depend on the target itself (totale hoogte = geleider + kapmaat),
  // so evaluate it per candidate choice.
  (target?.choiceConfig?.choices || []).forEach((choice) => {
    const candidate = { ...values, [targetFieldId]: choice.id };
    const rowValue = resolve(constraint.rowOptionId, rowIsChoice, candidate);
    if (rowValue === null) return;
    const matchedRow = rowIsChoice ? rowValue : roundUpToTier(rowValue, rowTiers);
    if (matchedRow === null) return;
    if (allowedForRow(matchedRow).includes(choice.id)) allowed.add(choice.id);
  });

  return allowed;
}

function evaluateChoiceRule(product, rule, values) {
  const refId = rule.optionId || rule.attribute;
  const operator = rule.operator || rule.operation;
  if (refId) {
    return evaluateCPQOptionRule({ ...rule, operator }, values, (id) => fieldIdFor(product, id));
  }
  // Already-mapped form rules point at the field id itself.
  if (rule.elementId) {
    return evaluateCPQOptionRule(
      { ...rule, optionId: rule.elementId, operator },
      values,
      (id) => id
    );
  }
  return true;
}

function evaluateGroup(product, group, values) {
  if (!group) return true;
  const ruleResults = (group.rules || []).map((rule) => evaluateChoiceRule(product, rule, values));
  const subResults = (group.subGroups || []).map((sub) => evaluateGroup(product, sub, values));
  const all = [...ruleResults, ...subResults];
  if (!all.length) return true;
  return group.logic === 'OR' ? all.some(Boolean) : all.every(Boolean);
}

/**
 * Choice conditions come either as an envelope ({ type, groups }) or as a
 * single group ({ logic, rules, subGroups }).
 */
export function choiceConditionsPass(product, conditions, values) {
  if (!conditions) return true;
  if (!Array.isArray(conditions.groups)) return evaluateGroup(product, conditions, values);
  if (!conditions.groups.length) return true;
  const results = conditions.groups.map((group) => evaluateGroup(product, group, values));
  const passed =
    conditions.betweenGroupLogic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  return conditions.type === 'hide' ? !passed : passed;
}

/**
 * @param {Object} product - camelCase product with options and choiceConstraints
 * @param {Object} values - configuration keyed by field id
 * @param {Object} deps
 * @param {Function} deps.resolveNumericDimension - (product, optionId, values) => number|null
 * @returns {Map<string, Set<string>>} optionId -> disabled choice ids (only options with any)
 */
export function getDisabledChoices(product, values, { resolveNumericDimension }) {
  const disabled = new Map();
  if (!product?.options?.length) return disabled;

  const disable = (optionId, choiceId) => {
    if (!disabled.has(optionId)) disabled.set(optionId, new Set());
    disabled.get(optionId).add(choiceId);
  };

  // A choice is allowed only when every constraint on its option allows it.
  (product.choiceConstraints || product.choice_constraints || []).forEach((constraint) => {
    if (!constraint?.targetOptionId || !constraint.grid?.length) return;
    const target = product.options.find((o) => o.id === constraint.targetOptionId);
    if (!target) return;
    const allowed = constraintAllowedIds(product, constraint, values, resolveNumericDimension);
    (target.choiceConfig?.choices || []).forEach((choice) => {
      if (!allowed.has(choice.id)) disable(target.id, choice.id);
    });
  });

  product.options.forEach((option) => {
    if (!isChoiceOption(option)) return;
    (option.choiceConfig?.choices || []).forEach((choice) => {
      if (choice.conditions && !choiceConditionsPass(product, choice.conditions, values)) {
        disable(option.id, choice.id);
      }
    });
  });

  return disabled;
}
